import { Alert, AlertDescription, Button, cn } from "@getgreenline/blaze-ui";
import type { ReactNode } from "react";

type StepErrorAlertProps = {
  message?: string;
  show?: boolean;
  tone?: "destructive" | "info" | "warning";
  leadingVisual?: ReactNode;
  dismissLabel?: string;
  onDismiss?: () => void;
};

export function StepAlert({
  message,
  show = false,
  tone = "destructive",
  leadingVisual,
  dismissLabel = "Dismiss",
  onDismiss,
}: StepErrorAlertProps) {
  if (!show || !message) {
    return null;
  }

  const isDestructive = tone === "destructive";
  const isWarning = tone === "warning";

  return (
    <Alert
      variant={isDestructive ? "destructive" : "default"}
      className={cn(
        "tw:mt-4 tw:!grid tw:items-center tw:gap-x-4 tw:gap-y-0 tw:rounded-xl",
        onDismiss
          ? "tw:!grid-cols-[0_minmax(0,1fr)_auto]"
          : "tw:!grid-cols-[0_minmax(0,1fr)]",
        isDestructive
          ? "tw:border-destructive/30 tw:bg-destructive/10 tw:text-destructive"
          : isWarning
            ? "tw:border-amber-200 tw:bg-amber-50 tw:text-amber-900"
            : "tw:border-sky-200 tw:bg-sky-50 tw:text-sky-900",
      )}
    >
      <AlertDescription
        className={cn(
          "tw:col-start-2 tw:flex tw:items-center tw:gap-2 tw:translate-y-0 tw:text-sm tw:font-medium",
          isDestructive
            ? "tw:text-destructive"
            : isWarning
              ? "tw:text-amber-900"
              : "tw:text-sky-900",
        )}
      >
        {leadingVisual ? (
          <span className="tw:shrink-0" aria-hidden="true">
            {leadingVisual}
          </span>
        ) : null}
        <span>{message}</span>
      </AlertDescription>
      {onDismiss ? (
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "tw:col-start-3 tw:row-start-1 tw:h-auto tw:cursor-pointer tw:self-center tw:px-2 tw:py-1 tw:text-sm",
            isDestructive
              ? "tw:text-destructive hover:tw:bg-destructive/10 hover:tw:text-destructive"
              : isWarning
                ? "tw:text-amber-900 hover:tw:bg-amber-100 hover:tw:text-amber-900"
                : "tw:text-sky-900 hover:tw:bg-sky-100 hover:tw:text-sky-900",
          )}
          onClick={onDismiss}
        >
          {dismissLabel}
        </Button>
      ) : null}
    </Alert>
  );
}
